import { apiFetch } from './api';

// ─── TYPES ──────────────────────────────────────────────────────────────────

export interface Home {
  id: string;
  tenantId: string;
  name: string | null;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  yearBuilt: number | null;
  sqft: number | null;
  imageUrl: string | null;
  createdAt: string;
  updatedAt: string;
  role?: 'owner' | 'member' | 'viewer';
  /** null means owner (all services allowed); array means family member with restricted access */
  grantedServiceTypes?: string[] | null;
  /** 'owner' for owned homes, 'family_member' for homes the user is a member of */
  accessRole?: 'owner' | 'family_member';
  memberRole?: string;
  /** Name of the primary home owner (null for owned homes) */
  ownerName?: string | null;
  assetCount?: number;
  issueCount?: number;
  maintenanceCount?: number;
  memberCount?: number;
}

export interface CreateHomeDto {
  name?: string;
  address: string;
  city: string;
  state?: string;
  zip?: string;
  country: string;
  yearBuilt?: number;
  sqft?: number;
  imageUrl?: string;
}

export interface UpdateHomeDto {
  name?: string;
  address?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  yearBuilt?: number;
  sqft?: number;
  imageUrl?: string;
}

// ─── API FUNCTIONS ──────────────────────────────────────────────────────────

// Backend wraps all responses in { success, data }
interface ApiResponse<T> {
  success: boolean;
  data: T;
}

/**
 * Create a new home
 */
export const createHome = async (data: CreateHomeDto): Promise<Home> => {
  const res = await apiFetch<ApiResponse<Home>>('/v1/homes', {
    method: 'POST',
    body: data,
  });
  return res.data;
};

/**
 * Get all homes for the current user
 */
export const getUserHomes = async (): Promise<Home[]> => {
  const res = await apiFetch<ApiResponse<Home[]>>('/v1/homes', {
    method: 'GET',
  });
  return res.data;
};

/**
 * Get a specific home by ID
 */
export const getHomeById = async (homeId: string): Promise<Home> => {
  const res = await apiFetch<ApiResponse<Home>>(`/v1/homes/${homeId}`, {
    method: 'GET',
  });
  return res.data;
};

/**
 * Update a home
 */
export const updateHome = async (homeId: string, data: UpdateHomeDto): Promise<Home> => {
  const res = await apiFetch<ApiResponse<Home>>(`/v1/homes/${homeId}`, {
    method: 'PUT',
    body: data,
  });
  return res.data;
};

/**
 * Delete a home
 */
export const deleteHome = async (homeId: string): Promise<{ message: string }> => {
  const res = await apiFetch<ApiResponse<{ message: string }>>(`/v1/homes/${homeId}`, {
    method: 'DELETE',
  });
  return res.data;
};

/**
 * Set a home as the primary/default home
 */
export const setPrimaryHome = async (homeId: string): Promise<{ message: string; homeId: string }> => {
  const res = await apiFetch<ApiResponse<{ message: string; homeId: string }>>(`/v1/homes/${homeId}/set-primary`, {
    method: 'POST',
  });
  return res.data;
};
